import { appendContactAsk, withoutAppendedContactAsk } from "./contactAsk";
import { figuresDiffer } from "./figures";
import { holdsOfferWording } from "./offerWords";
import { foldApostrophes } from "./scenario";
import { DAY_MS } from "./window";

/**
 * B6 graduation, as ruled on 8/21 (F-04, F-05, F-25).
 *
 * Three rulings, one module, because they only work together:
 *   F-04  graduation = human approval AND worst-of-k. Both, never either.
 *   F-05  "approved without edit" gets a definition with teeth.
 *   F-25  the numbers are FIRST-PASS, never post-retry.
 *
 * B6's standing rule sits above all of it: "Thresholds qualify; the HUMAN
 * flips the switch, always." Nothing here graduates anything. It reports
 * eligibility, and that is the whole contract.
 */

// ---------------------------------------------------------------------------
// F-05: approved without edit
// ---------------------------------------------------------------------------

/**
 * "Cosmetic changes (punctuation, greeting swaps, <=~10% normalized character
 * delta) still count as approved."
 *
 * F-05, v1 = 0.10: RATIFIED by Kabir 2026-09-27 (decision list item 24,
 * typed "ratify"). Implemented at the ruled value before that.
 */
export const COSMETIC_DELTA_MAX = 0.1;

export type EditKind = "unedited" | "cosmetic" | "substantive";

export type EditReason =
  | "identical"
  | "within_cosmetic_delta"
  | "figures_changed"
  | "meaning_tokens_changed"
  | "delta_exceeds_cosmetic"
  | "owner_flagged_substantive"
  | "owner_flagged_cosmetic";

export interface EditClassification {
  readonly kind: EditKind;
  readonly reason: EditReason;
  /** Character delta over the longer of the two texts, 0 to 1. */
  readonly normalizedDelta: number;
  /** True when the approved text makes a different numeric claim. */
  readonly figuresChanged: boolean;
  /**
   * Decision list item 97(a): the edit was measured against the draft with
   * code's appended contact ask set aside. Item 105: or against the approved
   * text with the ask added at approval set aside. Absent when it was not.
   */
  readonly contactAskSetAside?: true;
}

export interface EditOptions {
  /**
   * The owner's one-tap flag. The ruling gives it override force "either way",
   * because he is the one who knows whether he changed the meaning, bar one
   * thing: a changed figure is substantive whatever the tick says (TA-569).
   * A metric the owner cannot override gets gamed or ignored; one he can is a
   * measurement with a human in it.
   */
  readonly ownerFlag?: "cosmetic" | "substantive";
  /**
   * Decision list item 97(a), Kabir's 97a, ruled 2026-10-01: code appended the
   * fixed contact ask to this draft at compose (`drafts.contact_ask_appended`,
   * migration 0035). Absent or false is strict, as every draft was before.
   */
  readonly appendedContactAsk?: boolean;
  /**
   * Decision list item 105, Kabir's typed "yes recs are good", 2026-10-01
   * (TA-500): the owner added the fixed contact ask at approval
   * (`pnpm drafts approve --with-contact-ask`, item 102a). Set aside only when
   * the approved text is exactly the draft plus the ask. Absent or false is
   * strict.
   */
  readonly contactAskAddedAtApproval?: boolean;
}

/** Whitespace and case are noise for a distance comparison. */
function normalize(text: string): string {
  return text.trim().replace(/\s+/g, " ").toLowerCase();
}

/**
 * TA-1361 (F-05, CLAUDE.md section 13): the words that carry intent in a few
 * characters. "we can" to "we can't", "will" to "could": each is far inside
 * the 10% delta, and each is the owner correcting what the shop promised.
 * Read as whole words of the normalized text, so "can" inside "can't" or
 * "cannot" is not a second token. "this week" and "next week" are pairs.
 */
const NEGATION_TOKENS: ReadonlySet<string> = new Set([
  "not", "no", "never", "cannot",
  "can't", "won't", "don't", "isn't", "aren't", "wasn't", "doesn't", "didn't", "couldn't", "shouldn't", "wouldn't",
]);
const COMMITMENT_TOKENS: ReadonlySet<string> = new Set([
  "will", "can", "could", "guarantee", "guaranteed", "promise", "today", "tomorrow", "tonight",
]);
const COMMITMENT_PAIRS: ReadonlySet<string> = new Set(["this week", "next week"]);

/** The negation and commitment tokens of a normalized text, as a sorted multiset. */
function meaningTokens(normalized: string): string {
  const words = foldApostrophes(normalized).match(/[a-z]+(?:'[a-z]+)?/g) ?? [];
  const found: string[] = [];
  words.forEach((w, i) => {
    if (NEGATION_TOKENS.has(w) || COMMITMENT_TOKENS.has(w)) found.push(w);
    const pair = i > 0 ? `${words[i - 1] ?? ""} ${w}` : "";
    if (COMMITMENT_PAIRS.has(pair)) found.push(pair);
  });
  return found.sort().join("|");
}

/**
 * TA-1361: true when the approved text changes a negation, a commitment, or
 * whether an offer is read at all. The offer side is the shared reader
 * (offerWords.ts, section 0.5), never a second word list.
 */
function meaningChanged(draft: string, approved: string, a: string, b: string): boolean {
  if (meaningTokens(a) !== meaningTokens(b)) return true;
  return (holdsOfferWording(draft) !== null) !== (holdsOfferWording(approved) !== null);
}

/** Levenshtein, two-row. Drafts are DM-length, so this is not a hot path. */
function editDistance(a: string, b: string): number {
  if (a === b) return 0;
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;

  let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
  let curr = new Array<number>(b.length + 1);

  for (let i = 1; i <= a.length; i++) {
    curr[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(
        (curr[j - 1] ?? 0) + 1,
        (prev[j] ?? 0) + 1,
        (prev[j - 1] ?? 0) + cost,
      );
    }
    [prev, curr] = [curr, prev];
  }
  return prev[b.length] ?? 0;
}

/**
 * "Anything touching facts, offer, price, or intent is an edit."
 *
 * The figure check runs BEFORE the distance check and overrides it. A
 * character-distance rule on its own calls "$65" to "$85" cosmetic, two
 * characters out of fifty, and that is the single most expensive edit type in
 * the product: it is the owner correcting a wrong price, which is exactly the
 * event that must never count as "approved without edit".
 *
 * TA-1361: a negation, a commitment or an offer read in one text and not the
 * other is read next, also before the distance check, for the same reason.
 * It only ever moves an edit toward substantive (B13); COSMETIC_DELTA_MAX is
 * unchanged, and stored `edit_kind` rows are not re-read.
 */
export function classifyEdit(
  draft: string,
  approved: string,
  opts: EditOptions = {},
): EditClassification {
  const strict = classifyAgainst(draft, approved, opts.ownerFlag);
  // Decision list item 97(a), Kabir's 97a, ruled 2026-10-01. In grace code
  // appends the fixed contact ask to a composed reply (items 95a and 99b), so
  // an owner who deletes only that ask edited code's line, not the agent's
  // draft. Measured with the ask set aside, as a holding line is (TA-467).
  // Set aside only ever lowers the kind: an owner who keeps the ask and edits
  // elsewhere, or rewords it, is measured exactly as today, and on a tie the
  // strict reading, today's figures included, is the one returned.
  let best = strict;
  if (opts.appendedContactAsk === true) {
    const composed = withoutAppendedContactAsk(draft);
    if (composed !== null) best = lower(best, classifyAgainst(composed, approved, opts.ownerFlag));
  }
  // Decision list item 105 (TA-500): the ask the owner added at approval is
  // code's line too. Only the exact text the flag releases is set aside, so
  // the agent's draft is measured against itself; any other change is strict.
  if (opts.contactAskAddedAtApproval === true && approved === appendContactAsk(draft)) {
    best = lower(best, classifyAgainst(draft, draft, opts.ownerFlag));
  }
  return best;
}

/** The set-aside reading wins only when it is strictly lower; a tie keeps the strict one. */
function lower(current: EditClassification, setAside: EditClassification): EditClassification {
  return EDIT_KIND_RANK[setAside.kind] < EDIT_KIND_RANK[current.kind] ? { ...setAside, contactAskSetAside: true } : current;
}

const EDIT_KIND_RANK: Record<EditKind, number> = { unedited: 0, cosmetic: 1, substantive: 2 };

function classifyAgainst(
  draft: string,
  approved: string,
  ownerFlag: EditOptions["ownerFlag"],
): EditClassification {
  const a = normalize(draft);
  const b = normalize(approved);
  const longer = Math.max(a.length, b.length, 1);
  const normalizedDelta = editDistance(a, b) / longer;
  const figuresChanged = figuresDiffer(draft, approved, { side: "draft" });

  if (ownerFlag === "substantive") {
    return { kind: "substantive", reason: "owner_flagged_substantive", normalizedDelta, figuresChanged };
  }
  // F-05, TA-07: the figure check is read before the owner's cosmetic tick. A
  // changed price, count or turnaround is the owner correcting a claim, the one
  // event that must never be credited as "approved without edit", so a stray
  // "wording only" tap cannot override it (TA-569).
  if (figuresChanged) {
    return { kind: "substantive", reason: "figures_changed", normalizedDelta, figuresChanged };
  }
  if (ownerFlag === "cosmetic") {
    return { kind: "cosmetic", reason: "owner_flagged_cosmetic", normalizedDelta, figuresChanged };
  }
  if (a === b) {
    return { kind: "unedited", reason: "identical", normalizedDelta: 0, figuresChanged };
  }
  // TA-1361 (F-05: "anything touching facts, offer, price, or intent is an
  // edit"): a meaning change inside the delta is still an edit. After the
  // owner's ticks, as figures aside his tap beats the metric either way.
  if (meaningChanged(draft, approved, a, b)) {
    return { kind: "substantive", reason: "meaning_tokens_changed", normalizedDelta, figuresChanged };
  }
  if (normalizedDelta <= COSMETIC_DELTA_MAX) {
    return { kind: "cosmetic", reason: "within_cosmetic_delta", normalizedDelta, figuresChanged };
  }
  return { kind: "substantive", reason: "delta_exceeds_cosmetic", normalizedDelta, figuresChanged };
}

/** Cosmetic and unedited both count toward the approval rate (F-05). */
export function countsAsApprovedWithoutEdit(c: EditClassification): boolean {
  return c.kind !== "substantive";
}

// ---------------------------------------------------------------------------
// F-04 / F-25: eligibility
// ---------------------------------------------------------------------------

/** "Persistently >10% -> review, sustained -> auto-demote." */
export const REPAIR_RATE_REVIEW = 0.1;

/**
 * TA-241. F-25's over-bar decision, made on the exact counts and never on a
 * rate someone rounded for display. 21 repaired of 209 decided is 0.10048,
 * which three decimals show as 0.1: a check on that figure reads it as at
 * the bar and warns about nothing (section 0, silent degradation). Over
 * means strictly greater, the reading `autonomy.ts` already uses (`<=`
 * stays), so a rate exactly at the bar is not over it.
 *
 * Why the float division is the integer truth here (section 4, math through
 * deterministic code). Both `repaired / decided` and the literal behind
 * `REPAIR_RATE_REVIEW` are correctly rounded to the nearest double. When the
 * two rationals are equal (21 / 210 and 1 / 10) they round to the same
 * double, so `>` is false. When they differ, they differ by at least
 * 1 / (10 x decided), which stays far above one double step near 0.1 for any
 * decided below about 7e15, so rounding cannot carry one across the other.
 * `graduation.test.ts` also scans every pair up to 2000 decided against
 * `10 x repaired > decided`.
 *
 * Throws on anything that is not a pair of counts with repaired <= decided:
 * a malformed pair has no honest answer, and false would read as "within the
 * bar".
 */
export function repairRateExceedsReview(repaired: number, decided: number): boolean {
  if (
    !Number.isSafeInteger(repaired) ||
    !Number.isSafeInteger(decided) ||
    repaired < 0 ||
    repaired > decided
  ) {
    throw new Error(
      `repairRateExceedsReview: ${String(repaired)} repaired of ${String(decided)} decided is not a pair of counts. ` +
        "Pass whole numbers with 0 <= repaired <= decided, taken from the rows, never a rate.",
    );
  }
  if (decided === 0) return false;
  return repaired / decided > REPAIR_RATE_REVIEW;
}

/**
 * B6, both vault copies (Master Spec B6, Auto-Learn System Spec 6), verbatim:
 * "Repair acks: ~50 drafts @ >=95% approved-without-edit over 2+ weeks ->
 * eligible". The vault's own number, not a chosen one. B6 states the span
 * only on the repair-ack line; it is applied to every graduating class here
 * because a stricter bar (price talk, ~200 drafts @ 98%) earned over fewer
 * days than the looser one would invert B6's own "low stakes first" ordering.
 * That extension is the conservative reading and is logged for Kabir (B8).
 */
export const OBSERVATION_SPAN_MIN_DAYS = 14;

/**
 * B7 red-team pass, Planning elements agent-quality #3 and thinking.canvas
 * Runtime foundation #4: a scripted attack suite runs "before any scenario
 * graduates". `not_recorded` is its own value, never folded into `clean`:
 * a gate nobody ran is not a gate that held.
 */
export type RedTeamStatus = "clean" | "breached" | "not_recorded";

export interface ObservationSpan {
  /** The earliest decided draft this evidence rests on. */
  readonly start: Date;
  /** The latest decided draft this evidence rests on. */
  readonly end: Date;
}

export type GraduationBlocker =
  | "insufficient_volume"
  | "approval_rate_below_floor"
  | "worst_of_k_failed"
  | "first_pass_grounding_below_floor"
  | "observation_span_too_short"
  | "red_team_not_clean";

/**
 * TA-333. The blockers the approval half decides on its own, with no eval run:
 * volume, the approval floor, B6's span and B7's red team. The other two,
 * `worst_of_k_failed` and `first_pass_grounding_below_floor`, need a measured
 * eval half, and nothing measures one for a real agent yet.
 */
export type ApprovalHalfBlocker = Exclude<
  GraduationBlocker,
  "worst_of_k_failed" | "first_pass_grounding_below_floor"
>;

/**
 * TA-333. What a record is missing, as distinct from what blocks it. Its own
 * union beside `GraduationBlocker`, never a member of it, so
 * `evaluateGraduation` cannot emit it: a gap is not a verdict, and a list
 * that says `eval_not_measured` is a list that knows it is incomplete.
 */
export type GraduationEvidenceGap = "eval_not_measured";

/** F-25's early warning, the one warning `evaluateGraduation` raises. */
export type GraduationWarning = "repair_rate_high";

/**
 * The order `evaluateGraduation` has always listed its blockers in. Kept as
 * data so that splitting the approval half out (TA-333) cannot reorder a list
 * a human reads or a test pins.
 */
const BLOCKER_ORDER: readonly GraduationBlocker[] = [
  "insufficient_volume",
  "approval_rate_below_floor",
  "worst_of_k_failed",
  "first_pass_grounding_below_floor",
  "observation_span_too_short",
  "red_team_not_clean",
];

/**
 * True when the evidence covers B6's 2+ weeks. Null (no evidence, or no span
 * measured) is never long enough. A span that runs backwards, or carries an
 * invalid date, throws: it is a caller bug, and reporting a length for it
 * would be a number made up on the caller's behalf (section 0, fail loudly).
 */
export function observationSpanMet(span: ObservationSpan | null): boolean {
  if (span === null) return false;
  const start = span.start.getTime();
  const end = span.end.getTime();
  if (Number.isNaN(start) || Number.isNaN(end)) {
    throw new Error(
      "observationSpanMet: observation span has an invalid date. Pass the earliest and latest decidedAt of the evidence, or null when there is none.",
    );
  }
  if (end < start) {
    throw new Error(
      `observationSpanMet: observation span ends (${span.end.toISOString()}) before it starts (${span.start.toISOString()}). Pass the earliest decidedAt as start and the latest as end.`,
    );
  }
  return end - start >= OBSERVATION_SPAN_MIN_DAYS * DAY_MS;
}

export interface GraduationInput {
  readonly scenario: string;
  readonly graduationClass: "A" | "B" | "C" | "D";
  readonly draftsReviewed: number;
  readonly approvedWithoutEdit: number;
  /** B6: ~50 for repair acks, ~200 for price talk and custom leads. */
  readonly minDrafts: number;
  /** B6: 0.95 for repair acks, 0.98 for price talk. */
  readonly approvalFloor: number;
  readonly worstOfKPassed: boolean;
  /**
   * FIRST-PASS grounding, never post-retry (F-25). Passing the final number
   * here is the mistake the ruling exists to prevent, so the field name says
   * which one it wants.
   */
  readonly firstPassGroundingScore: number;
  /** B6 v1 default: 0.95. */
  readonly groundingFloor: number;
  /**
   * F-25: how many of `draftsReviewed` needed a corrective retry. A count,
   * never a rate (TA-241): the repair-rate warning is decided on this and
   * `draftsReviewed`, so no rounded figure can hide a rate over the bar.
   */
  readonly repairedDrafts: number;
  /**
   * B6 "over 2+ weeks": first and last decision the approval evidence covers.
   * Required, not optional, so no caller can graduate an agent by leaving it
   * out. Null when there is no evidence, which blocks.
   */
  readonly observationSpan: ObservationSpan | null;
  /** B7 red-team release gate. Only `clean` clears. */
  readonly redTeam: RedTeamStatus;
}

export interface GraduationResult {
  readonly eligible: boolean;
  /** Never "graduated". B6: the human flips the switch, always. */
  readonly decision: "eligible_pending_human" | "not_eligible";
  readonly blockers: readonly GraduationBlocker[];
  readonly warnings: readonly string[];
  readonly approvalRate: number;
}

/**
 * TA-333. The fields the approval half reads. `GraduationInput` carries every
 * one of them, so `evaluateGraduation` passes its own input straight through.
 */
export type ApprovalHalfInput = Pick<
  GraduationInput,
  | "draftsReviewed"
  | "approvedWithoutEdit"
  | "minDrafts"
  | "approvalFloor"
  | "repairedDrafts"
  | "observationSpan"
  | "redTeam"
>;

export interface ApprovalHalfFindings {
  /** In `evaluateGraduation`'s own order. */
  readonly blockers: readonly ApprovalHalfBlocker[];
  readonly warnings: readonly GraduationWarning[];
  /** Rounded to three decimals for reading. Decisions are made on the counts. */
  readonly approvalRate: number;
}

/**
 * TA-333. The approval half of B6, and the only place its rules live: volume,
 * the approval floor, the 2+ week span, the red team, and F-25's repair
 * warning. `evaluateGraduation` calls this and adds the eval half's two bars;
 * `knownGraduationBlockers` calls it when there is no eval half to add. One
 * copy of each rule, so a record read before the eval half exists can never
 * disagree with the verdict it would get after.
 *
 * Reports findings only. It never says eligible: without the eval half there
 * is nothing that could (F-04, both bars, never either).
 */
export function evaluateApprovalHalf(input: ApprovalHalfInput): ApprovalHalfFindings {
  const blockers: ApprovalHalfBlocker[] = [];
  const warnings: GraduationWarning[] = [];

  const approvalRate =
    input.draftsReviewed === 0 ? 0 : input.approvedWithoutEdit / input.draftsReviewed;

  if (input.draftsReviewed < input.minDrafts) blockers.push("insufficient_volume");
  if (approvalRate < input.approvalFloor) blockers.push("approval_rate_below_floor");

  // B6 "2+ weeks": volume alone lets a burst of approvals in a few days
  // qualify an agent. Every graduating class, see OBSERVATION_SPAN_MIN_DAYS.
  // This closes a burst with nothing older behind it, NOT a burst anchored by
  // one old decision: the span is earliest to latest, with no spread or
  // per-version requirement, so one day-0 decision plus an hour of approvals
  // on day 14 still clears it. That gap is pinned by a test and logged for
  // Kabir (B8), not closed by a spread rule the vault does not state.
  if (!observationSpanMet(input.observationSpan)) blockers.push("observation_span_too_short");

  // B7: "before any scenario graduates". Breached and never-run both block;
  // "when unsure, don't" (B13) reads an absent gate as a closed one.
  if (input.redTeam !== "clean") blockers.push("red_team_not_clean");

  // Its own early-warning metric, deliberately NOT a bar (F-25). A high repair
  // rate with a clean first pass is a contradiction worth a human's attention,
  // not an automatic no. Decided on the exact counts (TA-241).
  if (repairRateExceedsReview(input.repairedDrafts, input.draftsReviewed)) {
    warnings.push("repair_rate_high");
  }

  return {
    blockers,
    warnings,
    approvalRate: Math.round(approvalRate * 1000) / 1000,
  };
}

export function evaluateGraduation(input: GraduationInput): GraduationResult {
  // TA-333: the approval half's rules live in evaluateApprovalHalf, once.
  const approvalHalf = evaluateApprovalHalf(input);
  const blockers: GraduationBlocker[] = [...approvalHalf.blockers];

  // Both bars, AND not OR (F-04). They measure different things: humans see
  // the median, worst-of-k sees the tail. A scenario can look excellent to an
  // owner and still have a sample that would embarrass the shop.
  if (!input.worstOfKPassed) blockers.push("worst_of_k_failed");

  if (input.firstPassGroundingScore < input.groundingFloor) {
    blockers.push("first_pass_grounding_below_floor");
  }

  blockers.sort((a, b) => BLOCKER_ORDER.indexOf(a) - BLOCKER_ORDER.indexOf(b));

  const eligible = blockers.length === 0;
  return {
    eligible,
    decision: eligible ? "eligible_pending_human" : "not_eligible",
    blockers,
    warnings: [...approvalHalf.warnings],
    approvalRate: approvalHalf.approvalRate,
  };
}
